import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Clock, Upload } from 'lucide-react';

import { useIntake } from '../context/IntakeContext';
import { useStepNav } from '../hooks/useStepNav';
import { isSupportedImage } from '../lib/images';
import StepLayout from '../components/StepLayout';
import CameraCapture from '../components/CameraCapture';
import PhotoViewer from '../components/PhotoViewer';
import {
  ANGLE_SILHOUETTES,
  AngleIllustration,
  DamageIllustration,
  RegoTicket,
  RegoTicketOverlay
} from '../components/PhotoPlaceholders';
import { cn } from '../lib/utils';

/**
 * Step 5 — photos.
 *
 * The step itself only asks HOW: take them now, upload them, or skip. Doing it
 * happens after Continue, so the choice screen stays a choice screen.
 *
 * The old form made eight guided angles plus a registration shot plus three
 * damage close-ups compulsory before the review page could be reached, with no
 * upload path at all. Here every route is optional and the request has already
 * been saved, so "I haven't got time for this" is a supported answer rather
 * than an abandoned submission.
 */

/**
 * The shots worth having, used for BOTH the guided sequence and the upload
 * slots — one definition, so the two paths always ask for the same things.
 */
const SHOTS = [
  ...ANGLE_SILHOUETTES.map((angle) => ({
    key: angle.key,
    category: 'vehicle',
    label: `Angle — ${angle.key}`,
    hint: `${angle.key} of the car`,
    overlay: angle.src,
    Illustration: () => <AngleIllustration src={angle.src} alt={angle.key} />
  })),
  {
    key: 'Registration',
    category: 'registration',
    label: 'Registration ticket',
    hint: 'The registration ticket in the windscreen',
    optional: true,
    OverlayElement: RegoTicketOverlay,
    Illustration: () => <RegoTicket className="h-full w-full" />
  },
  ...[1, 2, 3].map((n) => ({
    key: `Damage ${n}`,
    category: 'damage',
    label: `Damage close-up ${n}`,
    hint: n === 1 ? 'Close-up of the damage' : `Another angle of the damage (${n} of 3)`,
    optional: true,
    Illustration: DamageIllustration
  }))
];

/** How the slots are grouped in the upload view. */
const GROUPS = [
  { title: 'Around the car', keys: ANGLE_SILHOUETTES.map((angle) => angle.key) },
  { title: 'The damage, close up', keys: ['Damage 1', 'Damage 2', 'Damage 3'] },
  { title: 'Registration ticket', keys: ['Registration'] }
];

const OPTIONS = [
  { id: 'now', icon: Camera, title: 'Take photos now' },
  { id: 'upload', icon: Upload, title: 'Upload photos' },
  { id: 'skip', icon: Clock, title: 'Skip for now' }
];

/** What the shop is told to expect. 'skip' still means photos may follow. */
const INTENT = { now: 'now', upload: 'upload', skip: 'later' };

/** Back the other way, for a customer resuming. */
const OPTION_FOR_INTENT = {
  now: 'now', upload: 'upload', later: 'skip', shop: 'skip', none: 'skip'
};

const OptionCard = ({ option, selected, onSelect }) => {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl p-4 text-left transition-colors',
        selected
          ? 'bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)]'
          : 'bg-muted/50 hover:bg-muted'
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span className="min-w-0 font-medium">{option.title}</span>
    </button>
  );
};

/**
 * One upload slot: the illustration IS the button. Tapping it picks a photo for
 * that specific shot; once filled it shows the photo and opens the viewer.
 */
const Slot = ({ shot, photo, onPick, onOpen }) => {
  const inputRef = useRef(null);
  const { Illustration } = shot;

  return (
    <>
      <button
        type="button"
        onClick={() => (photo ? onOpen(photo) : inputRef.current?.click())}
        aria-label={photo ? `View ${shot.key}` : `Add a photo: ${shot.key}`}
        className={cn(
          'relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg p-1 transition-colors',
          photo ? 'bg-muted' : 'bg-muted/50 hover:bg-muted'
        )}
      >
        {photo ? (
          photo.preview
            ? <img src={photo.preview} alt={shot.key} className="h-full w-full object-cover" />
            : <span className="text-[10px] text-muted-foreground">Added</span>
        ) : (
          <Illustration />
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onPick(shot, file);
        }}
      />
    </>
  );
};

const PhotosStep = () => {
  const { form, update, save, photos, uploadPhoto, replacePhoto, removePhoto, loadMissingPreviews } = useIntake();
  const go = useStepNav();

  const [mode, setMode] = useState(OPTION_FOR_INTENT[form.photosPromised] || '');
  const [phase, setPhase] = useState('choose');      // 'choose' | 'upload'
  const [shotIndex, setShotIndex] = useState(null);  // guided camera position
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  // A customer resuming has photo records but no local previews — without this
  // the slots would show empty tiles for photos they already sent.
  useEffect(() => { loadMissingPreviews(); }, [loadMissingPreviews]);

  /** The photo filling a given slot, matched on the label the shot was saved with. */
  const photoForShot = (shot) => photos.find((photo) => photo.label === shot.label);

  // ---- Guided capture ----

  const currentShot = shotIndex !== null ? SHOTS[shotIndex] : null;

  const advance = useCallback(() => {
    setShotIndex((index) => {
      if (index + 1 < SHOTS.length) return index + 1;
      return null;
    });
  }, []);

  const handleGuidedCapture = useCallback(async (source) => {
    if (!currentShot) return;

    setBusy(true);
    const existing = photos.find((photo) => photo.label === currentShot.label);
    const result = existing
      ? await replacePhoto(existing.id, source)
      : await uploadPhoto(source, { category: currentShot.category, label: currentShot.label });
    setBusy(false);

    if (!result.ok) toast.error(result.error || 'That photo did not upload — you can retry it below');

    // Advance regardless: a failed upload can be retried from the slot, and
    // stopping the sequence to deal with it would lose the customer.
    advance();
  }, [currentShot, photos, uploadPhoto, replacePhoto, advance]);

  // ---- Picking a file for one slot ----

  const handlePick = async (shot, file) => {
    if (!isSupportedImage(file)) {
      toast.error('That file is not a photo');
      return;
    }

    setBusy(true);
    const existing = photos.find((photo) => photo.label === shot.label);
    const result = existing
      ? await replacePhoto(existing.id, file)
      : await uploadPhoto(file, { category: shot.category, label: shot.label });
    setBusy(false);

    if (!result.ok) toast.error(result.error || 'That photo did not upload');
  };

  // ---- Viewer actions ----

  const handleReplace = async (file) => {
    const target = viewing;
    setViewing(null);
    setBusy(true);
    const result = await replacePhoto(target.id, file);
    setBusy(false);
    if (!result.ok) toast.error(result.error || 'That photo did not upload');
  };

  const handleDelete = async () => {
    const target = viewing;
    setViewing(null);
    try {
      await removePhoto(target.id);
    } catch (err) {
      toast.error(err.message || 'Could not remove that photo');
    }
  };

  // ---- Screens ----

  if (currentShot) {
    const { OverlayElement } = currentShot;
    return (
      <CameraCapture
        overlay={currentShot.overlay}
        overlayElement={OverlayElement ? <OverlayElement /> : null}
        hint={`${shotIndex + 1} of ${SHOTS.length} — ${currentShot.hint}`}
        busy={busy}
        onCapture={handleGuidedCapture}
        onSkip={currentShot.optional ? advance : undefined}
        onClose={() => { setShotIndex(null); go('review'); }}
      />
    );
  }

  if (viewing) {
    return (
      <PhotoViewer
        photo={viewing}
        busy={busy}
        onReplace={handleReplace}
        onDelete={handleDelete}
        onClose={() => setViewing(null)}
      />
    );
  }

  if (phase === 'upload') {
    return (
      <StepLayout
        title="Add your photos"
        onBack={() => setPhase('choose')}
        onNext={() => { save(); go('review'); }}
        busy={busy}
      >
        {GROUPS.map((group) => (
          <div key={group.title} className="flex flex-col gap-2">
            <p className="text-sm font-medium">{group.title}</p>
            <div className="grid grid-cols-4 gap-2">
              {group.keys.map((key) => {
                const shot = SHOTS.find((item) => item.key === key);
                return (
                  <Slot
                    key={key}
                    shot={shot}
                    photo={photoForShot(shot)}
                    onPick={handlePick}
                    onOpen={setViewing}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </StepLayout>
    );
  }

  const handleContinue = () => {
    save();

    if (mode === 'now') { setShotIndex(0); return; }
    if (mode === 'upload') { setPhase('upload'); return; }
    go('review');
  };

  return (
    <StepLayout
      title="Photos"
      onBack={() => go('workshop')}
      onNext={handleContinue}
      nextDisabled={!mode}
    >
      <div className="flex flex-col gap-2">
        {OPTIONS.map((option) => (
          <OptionCard
            key={option.id}
            option={option}
            selected={mode === option.id}
            onSelect={(id) => {
              setMode(id);
              update({ photosPromised: INTENT[id] || id });
            }}
          />
        ))}
      </div>

      {photos.length > 0 && (
        <button
          type="button"
          onClick={() => setPhase('upload')}
          className="flex items-center justify-center gap-1.5 self-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Review the {photos.length} photo{photos.length === 1 ? '' : 's'} you have added
        </button>
      )}
    </StepLayout>
  );
};

export default PhotosStep;
